import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { BOOKING_URL, scrollToSection } from "@/lib/site";
import { useLang } from "@/i18n";
import { EASE } from "@/components/Reveal";
import { IMAGES } from "@/content/images";

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
    <motion.span className="block" initial={{ y: "112%" }} animate={{ y: 0 }}
      transition={{ duration: 1.15, delay, ease: EASE }}>
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const ref = useRef(null);
  const { t } = useLang();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.65], [1, 0]);
  const contentY = useTransform(scrollYProgress, [0, 0.65], [0, 60]);

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative h-[100svh] min-h-[620px] overflow-hidden">
      <motion.div className="absolute inset-0" style={{ y: imgY }}>
        <motion.img
          src={IMAGES.hero}
          alt="Two women carrying yoga mats along the ocean at sunset in Cabo San Lucas"
          data-testid="hero-image"
          initial={{ scale: 1.14 }} animate={{ scale: 1 }} transition={{ duration: 2.2, ease: EASE }}
          className="h-full w-full object-cover object-[center_35%]"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-charcoal/70 via-charcoal/20 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-r from-charcoal/30 via-transparent to-transparent" />

      <motion.div style={{ opacity: contentOpacity, y: contentY }}
        className="relative z-10 flex h-full flex-col justify-end px-6 pb-14 md:px-12 md:pb-16 max-w-[1600px] mx-auto w-full">
        <h1 data-testid="hero-headline"
          className="font-display text-ivory leading-[0.92] tracking-[-0.01em] text-[15vw] sm:text-[13vw] lg:text-[8.4vw]">
          {t.hero.lines.map((line, i) => (
            <Line key={line} delay={0.45 + i * 0.13}>
              {i === t.hero.lines.length - 1 ? <span className="italic font-light">{line}</span> : line}
            </Line>
          ))}
        </h1>

        <div className="mt-10 md:mt-12 flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.05, ease: EASE }}
            className="max-w-md text-sm md:text-base leading-relaxed text-ivory/85">
            {t.hero.sub}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.2, ease: EASE }}
            className="flex flex-wrap items-center gap-4">
            <a href={BOOKING_URL} data-testid="hero-view-schedule-button" className="btn-pill btn-primary">
              {t.hero.ctaPrimary}
            </a>
            <a href="#private-experiences" data-testid="hero-private-experiences-button"
              onClick={(e) => { e.preventDefault(); scrollToSection("#private-experiences"); }}
              className="btn-pill btn-outline-light">
              {t.hero.ctaSecondary}
            </a>
          </motion.div>
        </div>
      </motion.div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-6 left-1/2 z-10 -translate-x-1/2 hidden md:block">
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
          className="h-10 w-px bg-ivory/50" />
      </motion.div>
    </section>
  );
};

export default Hero;
